import React from 'react';
import { X, Trophy, Users, School, Activity, UserCheck, Target } from 'lucide-react';

const BAR_COLORS = {
  'Modified Male': '#2196F3',
  'Modified Female': '#4FC3F7',
  'Junior Hardball Male': '#0B3D91',
  'Junior Hardball Female': '#E31837',
  'Senior Hardball Male': '#F59E0B',
  'Senior Hardball Female': '#10B981',
};

const PATHWAY_LABELS = [
  { key: 'modified_male', label: 'Modified Male' },
  { key: 'modified_female', label: 'Modified Female' },
  { key: 'junior_male', label: 'Junior Male' },
  { key: 'junior_female', label: 'Junior Female' },
  { key: 'senior_male', label: 'Senior Male' },
  { key: 'senior_female', label: 'Senior Female' },
];

/**
 * Map side panel: header + Involvement (Data Capture only) + Competitions (all other ICC sheets).
 * Datasets are not blended; only region name aligns them.
 *
 * @param {{ regionDetail: object, regionData?: object, onClose: () => void }} props
 */
export default function RegionDetailPanel({ regionDetail, regionData, onClose }) {
  const meta = regionDetail?.regionMeta || regionData || {};
  const inv = regionDetail?.involvement || {};
  const comp = regionDetail?.competitions || {};

  const breakdown = comp.competition_breakdown || [];
  const ageBreakdown = comp.age_breakdown || {};
  const ballBreakdown = comp.ball_breakdown || {};
  const uniqueTeams = comp.unique_teams || 0;
  const uniqueComps = comp.unique_competitions || 0;
  const totalEntries = comp.total_entries || 0;
  const peopleRoles = comp.people_roles || {};
  const pathwayCounts = comp.pathwayCounts || {};
  const teachers = comp.teachers ?? 0;

  const schools = inv.schools || [];
  const sessionOrgs = inv.session_organisations || {};
  const activityTypes = inv.activity_types || {};
  const deliverers = inv.deliverers || {};
  const sessionsCount = inv.sessionReports || 0;

  const maxComp = Math.max(...breakdown.map(b => b.count), 1);
  const ageEntries = Object.entries(ageBreakdown).sort((a, b) => b[1] - a[1]);

  const pathwayRows = PATHWAY_LABELS.map(({ key, label }) => ({
    label,
    val: pathwayCounts[key] || 0,
  })).filter((c) => c.val > 0);
  const maxPathway = Math.max(1, ...PATHWAY_LABELS.map(({ key }) => pathwayCounts[key] || 0));

  const peopleTotal = Object.values(peopleRoles).reduce((s, v) => s + v, 0);

  return (
    <div data-testid="region-detail">
      <div className="region-header">
        <div>
          <div className="region-name" data-testid="region-detail-name">{meta.name}</div>
          <div className="region-capital">{meta.cities || meta.capital}</div>
        </div>
        <button type="button" className="region-close" onClick={onClose} data-testid="region-close-btn">
          <X size={20} />
        </button>
      </div>

      {/* Involvement — Data Capture export only */}
      <div className="region-dataset-block region-dataset-involvement">
        <div className="region-dataset-heading">Involvement</div>
        <p className="region-dataset-note">Data Capture Tool export (sessions) — not linked to competition rows.</p>
        <div className="region-stats-grid">
          <div className="region-stat">
            <div className="region-stat-val">{(inv.participantsTotal || 0).toLocaleString()}</div>
            <div className="region-stat-label">Participants</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{(sessionsCount || 0).toLocaleString()}</div>
            <div className="region-stat-label">Session reports</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{(inv.numSessionsDelivery || 0).toLocaleString()}</div>
            <div className="region-stat-label">Delivery instances</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{schools.length.toLocaleString()}</div>
            <div className="region-stat-label">Schools / orgs</div>
          </div>
        </div>
        {(inv.male > 0 || inv.female > 0 || inv.disabled > 0) && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Male {inv.male.toLocaleString()} · Female {inv.female.toLocaleString()}
            {inv.disabled > 0 ? ` · Disabled ${inv.disabled.toLocaleString()}` : ''}
          </div>
        )}

        {sessionsCount > 0 && (
          <div style={{ marginTop: '0.5rem' }}>
            {Object.keys(deliverers).length > 0 && (
              <div style={{ marginBottom: '0.5rem' }}>
                <div className="region-section-title"><Activity size={16} /> Development officers</div>
                {Object.entries(deliverers).map(([name, count]) => (
                  <div
                    key={name}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.25rem 0',
                      fontSize: '0.78rem',
                      borderBottom: '1px solid rgba(0,0,0,0.03)',
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>{name}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            )}
            {Object.keys(activityTypes).length > 0 && (
              <div>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>ACTIVITY TYPES</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {Object.entries(activityTypes).slice(0, 8).map(([type, count], i) => (
                    <span
                      key={i}
                      style={{
                        padding: '0.15rem 0.45rem',
                        borderRadius: 10,
                        background: 'rgba(16,185,129,0.08)',
                        fontSize: '0.68rem',
                        color: '#059669',
                        fontWeight: 500,
                      }}
                    >
                      {type.length > 25 ? `${type.slice(0, 25)}…` : type}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {Object.keys(sessionOrgs).length > 0 && (
              <div style={{ marginTop: '0.5rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '0.25rem' }}>TOP ORGANISATIONS</div>
                {Object.entries(sessionOrgs).slice(0, 6).map(([org, count]) => (
                  <div
                    key={org}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.2rem 0',
                      fontSize: '0.75rem',
                      borderBottom: '1px solid rgba(0,0,0,0.04)',
                    }}
                  >
                    <span>{org.length > 28 ? `${org.slice(0, 28)}…` : org}</span>
                    <span style={{ fontWeight: 600 }}>{count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {schools.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="region-section-title"><School size={16} /> Schools ({schools.length})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
              {schools.slice(0, 12).map((s, i) => (
                <span
                  key={i}
                  style={{
                    padding: '0.15rem 0.5rem',
                    borderRadius: 12,
                    background: 'rgba(16,185,129,0.08)',
                    fontSize: '0.68rem',
                    color: 'var(--text-secondary)',
                    fontWeight: 500,
                  }}
                >
                  {s.name}
                </span>
              ))}
              {schools.length > 12 && (
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', padding: '0.15rem' }}>
                  +{schools.length - 12} more
                </span>
              )}
            </div>
          </div>
        )}

        {sessionsCount === 0 && schools.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.5rem' }}>
            No Data Capture session rows for this region.
          </p>
        )}
      </div>

      {/* Competitions — templates, people register, etc. */}
      <div className="region-dataset-block region-dataset-competitions">
        <div className="region-dataset-heading">Competitions & ICC registers</div>
        <p className="region-dataset-note">Competition templates and people/match sources — not linked to session participants.</p>

        <div className="region-stats-grid">
          <div className="region-stat">
            <div className="region-stat-val">{totalEntries.toLocaleString()}</div>
            <div className="region-stat-label">Competition entries</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{uniqueTeams.toLocaleString()}</div>
            <div className="region-stat-label">Unique teams</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{uniqueComps.toLocaleString()}</div>
            <div className="region-stat-label">Competitions</div>
          </div>
          <div className="region-stat">
            <div className="region-stat-val">{peopleTotal.toLocaleString()}</div>
            <div className="region-stat-label">People (roles)</div>
          </div>
        </div>
        {teachers > 0 && (
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
            Teachers / coaches (register): {teachers.toLocaleString()}
          </div>
        )}

        {totalEntries > 0 && (
          <div
            style={{
              margin: '0.5rem 0',
              padding: '0.6rem',
              borderRadius: 'var(--radius-sm)',
              background: 'rgba(11,61,145,0.03)',
              fontSize: '0.78rem',
              color: 'var(--text-secondary)',
            }}
          >
            <strong>{totalEntries.toLocaleString()}</strong> team rows · <strong>{uniqueTeams.toLocaleString()}</strong> unique teams ·{' '}
            <strong>{uniqueComps.toLocaleString()}</strong> competition names
          </div>
        )}

        {breakdown.length > 0 && (
          <div>
            <div className="region-section-title"><Trophy size={16} /> Competition breakdown</div>
            {breakdown.map((b, i) => {
              const label = `${b.type} ${b.gender}`;
              return (
                <div key={i} className="mini-bar-row">
                  <div className="mini-bar-label">{label}</div>
                  <div className="mini-bar-track">
                    <div
                      className="mini-bar-fill"
                      style={{
                        width: `${(b.count / maxComp) * 100}%`,
                        background: BAR_COLORS[label] || '#0B3D91',
                      }}
                    />
                  </div>
                  <div className="mini-bar-value">{b.count}</div>
                </div>
              );
            })}
          </div>
        )}

        {ageEntries.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="region-section-title"><Target size={16} /> Age groups</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
              {ageEntries.map(([ag, count], i) => (
                <span
                  key={i}
                  style={{
                    padding: '0.2rem 0.55rem',
                    borderRadius: 12,
                    background: 'rgba(11,61,145,0.06)',
                    fontSize: '0.7rem',
                    color: 'var(--brand-primary)',
                    fontWeight: 600,
                  }}
                >
                  {ag}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {Object.keys(ballBreakdown).length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.75rem' }}>
              {Object.entries(ballBreakdown).map(([type, count], i) => (
                <span
                  key={i}
                  style={{
                    padding: '0.2rem 0.55rem',
                    borderRadius: 12,
                    background: type.includes('Soft') ? 'rgba(79,195,247,0.15)' : 'rgba(227,24,55,0.08)',
                    color: type.includes('Soft') ? '#0284C7' : '#E31837',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                >
                  {type.replace(' - outdoor', '')}: {count}
                </span>
              ))}
            </div>
          </div>
        )}

        {pathwayRows.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="region-section-title"><Users size={16} /> Pathway classification (row counts)</div>
            {pathwayRows.map((c, i) => (
              <div key={i} className="mini-bar-row">
                <div className="mini-bar-label">{c.label}</div>
                <div className="mini-bar-track">
                  <div
                    className="mini-bar-fill"
                    style={{
                      width: `${(c.val / maxPathway) * 100}%`,
                      background: BAR_COLORS[c.label] || '#0B3D91',
                    }}
                  />
                </div>
                <div className="mini-bar-value">{c.val.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {Object.keys(peopleRoles).length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            <div className="region-section-title"><UserCheck size={16} /> People by role</div>
            {Object.entries(peopleRoles).map(([role, count]) => (
              <div
                key={role}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '0.3rem 0',
                  borderBottom: '1px solid rgba(0,0,0,0.04)',
                  fontSize: '0.8rem',
                }}
              >
                <span>{role}</span>
                <span style={{ fontWeight: 700, color: 'var(--brand-primary)' }}>{count}</span>
              </div>
            ))}
          </div>
        )}

        {totalEntries === 0 && peopleTotal === 0 && pathwayRows.length === 0 && (
          <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '0.5rem' }}>
            No competition or people register rows for this region.
          </p>
        )}
      </div>
    </div>
  );
}
